/* eslint-disable camelcase */

import {
  createSlice,
  isFulfilled,
  isPending,
  isRejected,
  PayloadAction,
} from '@reduxjs/toolkit';

import {
  adminCreateLessonHistoryAdminNote,
  adminDeleteLessonHistoryAdminNote,
  adminGetLessonHistoryById,
  adminLessonHistoryListGet,
  adminRespondToLessonHistoryTutorPaymentConcern,
  adminUpdateLessonHistoryClassExtension,
  adminUpdateLessonHistoryTrialConversion,
  adminUpdateLessonHistoryTutorPaymentAdjustments,
  adminUpdateLessonHistoryTutorPaymentStatus,
  adminUpdateLessonHistoryTutorPenalty,
} from './adminApi';
import { RootState } from '../..';
import {
  getLessonHistoryById,
  lessonHistoryListGet,
  lessonHistoryRaiseConcern,
} from './api';
import {
  ISection,
  TLessonTypes,
  TMeetingSectionPaymentType,
} from '../SectionSlice';
import { ICourse } from '../CoursesSlice';
import { IUser, TIdentifiedUserRole } from '../../UserSlice';
import { TUserSelectOption } from '../../../components/Inputs/admin/UserListDropdown';
import { TCourseSelectOption } from '../../../components/Inputs/admin/CourseListDropdown';
import { IManualAdjustment } from '../ManualAdjustmentsSlice';

export const LESSON_HISTORY_TUTOR_PAYMENT_STATUSES = {
  PROCESSING: 'PROCESSING',
  PAID: 'PAID',
} as const;

export const LESSON_HISTORY_TUTOR_PAYMENT_CONCERN_STATUSES = {
  PENDING: 'PENDING',
  RESOLVED: 'RESOLVED',
  REJECTED: 'REJECTED',
} as const;

export const LESSON_HISTORY_CLASS_EXTENSION_REQUEST_STATUSES = {
  APPROVED: 'APPROVED',
  DECLINED: 'DECLINED',
} as const;

export type TLessonHistoryTutorPaymentStatus = typeof LESSON_HISTORY_TUTOR_PAYMENT_STATUSES[keyof typeof LESSON_HISTORY_TUTOR_PAYMENT_STATUSES];
export type TLessonHistoryTutorPaymentConcernStatus = typeof LESSON_HISTORY_TUTOR_PAYMENT_CONCERN_STATUSES[keyof typeof LESSON_HISTORY_TUTOR_PAYMENT_CONCERN_STATUSES];
export type TLessonHistoryClassExtensionRequestStatus = typeof LESSON_HISTORY_CLASS_EXTENSION_REQUEST_STATUSES[keyof typeof LESSON_HISTORY_CLASS_EXTENSION_REQUEST_STATUSES];
export type TLessonHistoryTutorAmountData = {
  manualAdjustments: Array<IManualAdjustment>;
  // Paid
  paidTotalData: { [key: string]: number };
  pendingTotalData: { [key: string]: number };
  paidTutorPenaltyData: { [key: string]: number };
  paidStudentPenaltyData: { [key: string]: number };
  paidPenaltyWaiverData: { [key: string]: number };
  manualAdjustmentData: { [key: string]: number };
  paidAmountData: { [key: string]: number };
  lessonHistoryPaidTutorPenalties: { [key: string]: number };
  // PENDING
  pendingTutorPenaltyData: { [key: string]: number };
  pendingStudentPenaltyData: { [key: string]: number };
  pendingPenaltyWaiverData: { [key: string]: number };
  pendingAmountData: { [key: string]: number };
  lessonHistoryPendingTutorPenalties: { [key: string]: number };
};

export interface ILessonHistoryParticipantAttendance {
  id: string;
  user_id: string;
  name: string;
  user_email: string;
  join_time: string;
  leave_time: string;
  duration: number;
  attentiveness_score: string;
  failover: boolean;
  status: string;
  groupId: string;
  customer_key: string;
  participant_user_id: string;
}

export interface ILessonHistoryAdminNote {
  _id: string;
  description: string;
  createdBy: string | IUser;
  createdAt: string;
}

export interface ILessonHistoryTutorPaymentConcern {
  concern: string;
  status: TLessonHistoryTutorPaymentConcernStatus;
  createdBy: string | IUser;
  createdAt: string;
  adminNote?: string;
  respondedBy?: string | IUser;
  respondedAt?: string;
}

export interface ILessonHistoryLog {
  _id: string;
  createdAt: string;
  createdBy: string | IUser;
  title: string;
}

export interface IStudentAmountData {
  amount: number;
  rate: number;
  currency: string;
}

export interface ILessonHistory {
  _id: string;
  section: string | ISection;
  sectionDetails: ISection;
  course: string | ICourse;
  subject: string;
  curriculum: string;
  grade: string;
  start: string;
  createdAt: string;
  students: IUser[];
  tutors: IUser[];
  duration: number;
  end?: string;
  identifiedUserRole?: TIdentifiedUserRole;
  attendanceHistory?: ILessonHistoryParticipantAttendance[];
  adminNotes?: ILessonHistoryAdminNote[];
  eventId?: string;
  studentJoinTime?: number;
  tutorJoinTime?: number;
  paymentTutor?: string | IUser;
  tutorRate?: number;
  tutorRateCurrency?: string;
  tutorAmount?: number;
  tutorAmountCurrency?: string;
  allocatedTutorAmount?: number;
  tutorPaymentStatus: TLessonHistoryTutorPaymentStatus;
  lessonType: TLessonTypes;
  paymentType: TMeetingSectionPaymentType;
  tutorPenalty?: number;
  tutorPenaltyAmount?: number;
  tutorPenaltyReason?: string;
  tutorPaymentConcern?: ILessonHistoryTutorPaymentConcern;
  lessonExtension?: number;
  lessonExtensionTutorAmount?: number;
  classExtensionRequestStatus?: TLessonHistoryClassExtensionRequestStatus;
  logs?: ILessonHistoryLog[];
  isTrialConverted?: boolean;
  trialConversionStatus?: string;
  referenceId: string;
  studentAmountData?: IStudentAmountData;
  grossProfitMargin?: number;
}

export interface IAdminLessonHistoryCommonFilterData {
  startDate?: string;
  endDate?: string;
  selectedStudent?: TUserSelectOption;
  selectedTutor?: TUserSelectOption;
  selectedCourse?: TCourseSelectOption;
  startDateCleared?: boolean;
  endDateCleared?: boolean;
}

export interface ILessonHistoryCommonFilterData {
  startDate?: string;
  endDate?: string;
  selectedStudent?: TUserSelectOption;
  selectedTutor?: TUserSelectOption;
  selectedCourse?: TCourseSelectOption;
  startDateCleared?: boolean;
  endDateCleared?: boolean;
}

export interface IInitialLessonHistoryState {
  status: 'idle' | 'loading' | 'rejected';
  lessonHistoryListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ILessonHistory>;
    tutorAmountData?: TLessonHistoryTutorAmountData;
  };
  lessonHistoryData?: ILessonHistory;
  lessonHistoryCommonFilterData?: ILessonHistoryCommonFilterData;
  // ADMIN
  adminLessonHistoryListData?: {
    limit?: number;
    offset?: number;
    totalDocs?: number;
    list: Array<ILessonHistory>;
    tutorAmountData?: TLessonHistoryTutorAmountData;
  };
  adminLessonHistoryData?: ILessonHistory;
  adminLessonHistoryCommonFilterData?: IAdminLessonHistoryCommonFilterData;
}

const initialState: IInitialLessonHistoryState = {
  status: 'idle',
  lessonHistoryListData: undefined,
  lessonHistoryData: undefined,
  // ADMIN
  adminLessonHistoryListData: undefined,
  adminLessonHistoryData: undefined,
};

export const LessonHistorySlice = createSlice({
  name: 'lessonHistory',
  initialState,
  reducers: {
    resetLessonHistoryData: (state) => {
      state.lessonHistoryData = undefined;
    },
    resetLessonHistoryListData: (state) => {
      state.lessonHistoryListData = undefined;
    },
    updateLessonHistoryCommonFilterData: (
      state,
      action: PayloadAction<Partial<ILessonHistoryCommonFilterData>>
    ) => {
      const newAttributes = action.payload || {};

      if (state.lessonHistoryCommonFilterData) {
        state.lessonHistoryCommonFilterData = {
          ...state.lessonHistoryCommonFilterData,
          ...newAttributes,
        };
      } else {
        state.lessonHistoryCommonFilterData = { ...newAttributes };
      }
    },
    resetLessonHistoryCommonFilterData: (state) => {
      state.lessonHistoryCommonFilterData = undefined;
    },
    // ADMIN
    resetAdminLessonHistoryListData: (state) => {
      state.adminLessonHistoryListData = undefined;
    },
    resetAdminLessonHistoryData: (state) => {
      state.adminLessonHistoryData = undefined;
    },
    updateAdminLessonHistoryCommonFilterData: (
      state,
      action: PayloadAction<Partial<IAdminLessonHistoryCommonFilterData>>
    ) => {
      const newAttributes = action.payload || {};

      if (state.adminLessonHistoryCommonFilterData) {
        state.adminLessonHistoryCommonFilterData = {
          ...state.adminLessonHistoryCommonFilterData,
          ...newAttributes,
        };
      } else {
        state.adminLessonHistoryCommonFilterData = { ...newAttributes };
      }
    },
    resetAdminLessonHistoryCommonFilterData: (state) => {
      state.adminLessonHistoryCommonFilterData = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(lessonHistoryListGet.fulfilled, (state, action) => {
        const result = action.payload;

        const newLessonHistoryListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
          tutorAmountData: result.tutorAmountData,
        };

        state.lessonHistoryListData = newLessonHistoryListData;
      })
      .addCase(getLessonHistoryById.fulfilled, (state, action) => {
        const result = action.payload;

        state.lessonHistoryData = result;
      })
      .addCase(lessonHistoryRaiseConcern.fulfilled, (state, action) => {
        const result = action.payload;

        state.lessonHistoryData = result;
      });

    // Admin
    builder
      .addCase(adminLessonHistoryListGet.fulfilled, (state, action) => {
        const result = action.payload;
        const newAdminLessonHistoryListData = {
          limit: result.limit,
          offset: result.offset,
          totalDocs: result.totalDocs || 0,
          list: result.docs || [],
          tutorAmountData: result.tutorAmountData,
        };

        state.adminLessonHistoryListData = newAdminLessonHistoryListData;
      })
      .addCase(adminGetLessonHistoryById.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminLessonHistoryData = result;
      })
      .addCase(adminCreateLessonHistoryAdminNote.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminLessonHistoryData = result;

        // Update the list data if it exists
        if (state.adminLessonHistoryListData) {
          const newAdminLessonHistoryListData = {
            ...state.adminLessonHistoryListData,
          };

          const newList = [...(newAdminLessonHistoryListData.list || [])];

          const index = newList.findIndex((item) => item._id === result._id);

          if (index > -1) {
            newList[index] = {
              ...newList[index],
              adminNotes: result.adminNotes,
            };

            newAdminLessonHistoryListData.list = newList;

            state.adminLessonHistoryListData = {
              ...newAdminLessonHistoryListData,
            };
          }
        }
      })
      .addCase(adminDeleteLessonHistoryAdminNote.fulfilled, (state, action) => {
        const result = action.payload;

        state.adminLessonHistoryData = result;

        // Update the list data if it exists
        if (state.adminLessonHistoryListData) {
          const newAdminLessonHistoryListData = {
            ...state.adminLessonHistoryListData,
          };

          const newList = [...(newAdminLessonHistoryListData.list || [])];

          const index = newList.findIndex((item) => item._id === result._id);

          if (index > -1) {
            newList[index] = {
              ...newList[index],
              adminNotes: result.adminNotes,
            };

            newAdminLessonHistoryListData.list = newList;

            state.adminLessonHistoryListData = {
              ...newAdminLessonHistoryListData,
            };
          }
        }
      })
      .addCase(
        adminUpdateLessonHistoryTutorPaymentStatus.fulfilled,
        (state, action) => {
          const result = action.payload;

          if (
            state.adminLessonHistoryListData &&
            result.tutorPaymentStatus &&
            result.lesssonHistoryIds &&
            result.lesssonHistoryIds.length
          ) {
            const newAdminLessonHistoryListData = {
              ...state.adminLessonHistoryListData,
            };

            const newList = [...state.adminLessonHistoryListData.list].map(
              (item) => {
                if ((result.lesssonHistoryIds || []).includes(item._id)) {
                  return {
                    ...item,
                    tutorPaymentStatus: result.tutorPaymentStatus,
                  };
                }

                return item;
              }
            );

            newAdminLessonHistoryListData.list = newList;

            state.adminLessonHistoryListData = newAdminLessonHistoryListData;
          }
        }
      );

    // TUTOR PAYMENT ADJUSTMENTS UPDATE
    builder.addMatcher(
      isFulfilled(
        adminUpdateLessonHistoryTutorPaymentAdjustments,
        adminRespondToLessonHistoryTutorPaymentConcern,
        adminUpdateLessonHistoryClassExtension,
        adminUpdateLessonHistoryTutorPaymentAdjustments,
        adminUpdateLessonHistoryTutorPenalty,
        adminUpdateLessonHistoryTrialConversion
      ),
      (state, action) => {
        const result = action.payload;

        state.adminLessonHistoryData = result;

        if (state.adminLessonHistoryListData) {
          const newAdminLessonHistoryListData = {
            ...state.adminLessonHistoryListData,
          };

          const newList = [...(newAdminLessonHistoryListData.list || [])];

          const index = newList.findIndex((item) => item._id === result._id);

          if (index > -1) {
            newList[index] = {
              ...newList[index],
              tutorRate: result.tutorRate,
              tutorRateCurrency: result.tutorRateCurrency,
              duration: result.duration,
              allocatedTutorAmount: result.allocatedTutorAmount,
              tutorAmount: result.tutorAmount,
              tutorAmountCurrency: result.tutorAmountCurrency,
              tutorPenalty: result.tutorPenalty,
              tutorPenaltyAmount: result.tutorPenaltyAmount,
              tutorPenaltyReason: result.tutorPenaltyReason,
              lessonExtension: result.lessonExtension,
              lessonExtensionTutorAmount: result.lessonExtensionTutorAmount,
              tutorPaymentStatus: result.tutorPaymentStatus,
              classExtensionRequestStatus: result.classExtensionRequestStatus,
              isTrialConverted: result.isTrialConverted,
              trialConversionStatus: result.trialConversionStatus,
            };

            newAdminLessonHistoryListData.list = newList;

            state.adminLessonHistoryListData = {
              ...newAdminLessonHistoryListData,
            };
          }
        }
      }
    );

    // Common Matchers
    builder.addMatcher(
      isFulfilled(
        lessonHistoryListGet,
        getLessonHistoryById,
        lessonHistoryRaiseConcern,
        // ADMIN
        adminLessonHistoryListGet,
        adminGetLessonHistoryById,
        adminCreateLessonHistoryAdminNote,
        adminDeleteLessonHistoryAdminNote,
        adminUpdateLessonHistoryTutorPaymentStatus,
        adminUpdateLessonHistoryTutorPaymentAdjustments,
        adminRespondToLessonHistoryTutorPaymentConcern,
        adminUpdateLessonHistoryClassExtension,
        adminUpdateLessonHistoryTutorPenalty,
        adminUpdateLessonHistoryTrialConversion
      ),
      (state) => {
        state.status = 'idle';
      }
    );

    builder.addMatcher(
      isPending(
        lessonHistoryListGet,
        getLessonHistoryById,
        lessonHistoryRaiseConcern,
        // Admin
        adminLessonHistoryListGet,
        adminGetLessonHistoryById,
        adminCreateLessonHistoryAdminNote,
        adminDeleteLessonHistoryAdminNote,
        adminUpdateLessonHistoryTutorPaymentStatus,
        adminUpdateLessonHistoryTutorPaymentAdjustments,
        adminRespondToLessonHistoryTutorPaymentConcern,
        adminUpdateLessonHistoryClassExtension,
        adminUpdateLessonHistoryTutorPenalty,
        adminUpdateLessonHistoryTrialConversion
      ),
      (state) => {
        state.status = 'loading';
      }
    );

    builder.addMatcher(
      isRejected(
        lessonHistoryListGet,
        getLessonHistoryById,
        lessonHistoryRaiseConcern,
        // ADMIN
        adminLessonHistoryListGet,
        adminGetLessonHistoryById,
        adminCreateLessonHistoryAdminNote,
        adminDeleteLessonHistoryAdminNote,
        adminUpdateLessonHistoryTutorPaymentStatus,
        adminUpdateLessonHistoryTutorPaymentAdjustments,
        adminRespondToLessonHistoryTutorPaymentConcern,
        adminUpdateLessonHistoryClassExtension,
        adminUpdateLessonHistoryTutorPenalty,
        adminUpdateLessonHistoryTrialConversion
      ),
      (state) => {
        state.status = 'rejected';
      }
    );
  },
});

export const getLessonHistoryState = (
  state: RootState
): RootState['lessonHistory'] => state.lessonHistory;

export const {
  resetLessonHistoryListData,
  resetLessonHistoryData,
  updateLessonHistoryCommonFilterData,
  resetLessonHistoryCommonFilterData,
  // Admin
  resetAdminLessonHistoryData,
  resetAdminLessonHistoryListData,
  updateAdminLessonHistoryCommonFilterData,
  resetAdminLessonHistoryCommonFilterData,
} = LessonHistorySlice.actions;

export * from './api';
export * from './adminApi';
export default LessonHistorySlice.reducer;
